import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  CalendarClock, Plus, Search, FileText, Users, BookOpen,
  Hash, UserSearch, Phone, User, X, CheckCircle, AlertTriangle,
  Loader2, Clock, Trash2, Eye, Edit3, XCircle, Briefcase,
} from "lucide-react";
import {
  AppButton, AppCard, AppCardHeader, AppCardTitle, AppCardContent,
  EmptyState, ConfirmModal,
} from "../ui";
import { Form, Spinner, Modal } from "react-bootstrap";
import apiClient from "../../utils/apiClient";
import moment from "moment";
import "./scheduled-messages.scss";

/* ── Constants ──────────────────────────────────────────────────────────────── */
const AUDIENCE_TYPES = [
  { value: "all_students", label: "All Students", icon: Users, desc: "Every student in your institute" },
  { value: "course", label: "By Course", icon: BookOpen, desc: "Students enrolled in a specific course" },
  { value: "batch", label: "By Batch", icon: Hash, desc: "Students in a specific batch" },
  { value: "specific_students", label: "Specific Students", icon: UserSearch, desc: "Select individual students" },
  { value: "external_numbers", label: "External Numbers", icon: Phone, desc: "Enter phone numbers manually" },
  { value: "trainers", label: "Trainers", icon: Briefcase, desc: "Institute trainers/instructors" },
];

const RECIPIENT_STATUS_OPTIONS = [
  { value: "active", label: "Active Only", icon: CheckCircle },
  { value: "inactive", label: "Inactive Only", icon: XCircle },
  { value: "all", label: "All (Active + Inactive)", icon: Users },
];

const STATUS_MAP = {
  scheduled: { label: "Scheduled", variant: "pending", color: "#f59e0b" },
  processing: { label: "Processing", variant: "pending", color: "#3b82f6" },
  sent: { label: "Sent", variant: "active", color: "#22c55e" },
  partially_sent: { label: "Partial", variant: "warning", color: "#f97316" },
  failed: { label: "Failed", variant: "destructive", color: "#ef4444" },
  cancelled: { label: "Cancelled", variant: "inactive", color: "#6b7280" },
};

const DYNAMIC_VARIABLES = [
  { value: "student_name", label: "Student Name" },
  { value: "course_name", label: "Course Name" },
  { value: "batch_name", label: "Batch Name" },
  { value: "institute_name", label: "Institute Name" },
  { value: "pending_fees", label: "Pending Fees" },
  { value: "current_date", label: "Current Date" },
];

const Loader = () => (
  <div className="wa-loader"><Loader2 size={20} className="wa-spin" /> Loading…</div>
);

/* ── Helpers ────────────────────────────────────────────────────────────────── */
function _extractParams(component) {
  if (!component?.text) return [];
  const params = [];
  const namedRegex = /\{\{([a-zA-Z_]\w*)\}\}/g;
  const positionalRegex = /\{\{(\d+)\}\}/g;
  let m;
  while ((m = namedRegex.exec(component.text)) !== null) {
    params.push({ key: m[1], label: m[1].replace(/_/g, ' '), example: component.example?.body_text?.[0]?.[params.length] || '' });
  }
  const posnMatches = [...component.text.matchAll(positionalRegex)];
  posnMatches.forEach(pm => {
    if (!params.find(p => p.key === pm[1])) {
      params.push({ key: pm[1], label: `Variable ${pm[1]}`, example: component.example?.body_text?.[0]?.[Number(pm[1]) - 1] || '' });
    }
  });
  return params;
}

function _extractBtnParams(btcComponent) {
  if (!btcComponent?.buttons) return [];
  const params = [];
  btcComponent.buttons.forEach((btn, idx) => {
    if (btn.type === 'URL' && btn.url) {
      const match = btn.url.match(/\{\{(\d+)\}\}/);
      if (match) {
        params.push({ key: `btn_${idx}_${match[1]}`, label: `Button "${btn.text}" URL suffix`, buttonIndex: idx, example: btn.example?.[0] || '', baseUrl: btn.url.replace(/\/?\{\{\d+\}\}$/, '') });
      }
    }
  });
  return params;
}

function _extractTemplateVars(template) {
  if (!template?.components) return { header: [], body: [], button: [] };
  const hc = template.components.find(c => c.type === "HEADER");
  const bc = template.components.find(c => c.type === "BODY");
  const btc = template.components.find(c => c.type === "BUTTONS");
  return { header: _extractParams(hc), body: _extractParams(bc), button: _extractBtnParams(btc) };
}

function _buildComponents(vars, values) {
  const components = [];
  const mkParam = (p, prefix) => {
    const param = { type: "text", text: values[`${prefix}_${p.key}`] || "" };
    if (!/^\d+$/.test(p.key)) param.parameter_name = p.key;
    return param;
  };
  if (vars.header.length) components.push({ type: "header", parameters: vars.header.map(p => mkParam(p, "header")) });
  if (vars.body.length) components.push({ type: "body", parameters: vars.body.map(p => mkParam(p, "body")) });
  if (vars.button.length) {
    const byBtn = {};
    vars.button.forEach(p => { if (!byBtn[p.buttonIndex]) byBtn[p.buttonIndex] = []; byBtn[p.buttonIndex].push(p); });
    Object.entries(byBtn).forEach(([idx, params]) => {
      components.push({ type: "button", sub_type: "url", index: Number(idx), parameters: params.map(p => mkParam(p, "button")) });
    });
  }
  return components;
}

function _fillPreview(text, params, values, prefix) {
  if (!text) return text;
  let result = text;
  params.forEach(p => { result = result.replace(`{{${p.key}}}`, values[`${prefix}_${p.key}`] || `{{${p.key}}}`); });
  return result;
}

const DYNAMIC_VAR_LABELS = { student_name: "Student Name", course_name: "Course Name", batch_name: "Batch Name", institute_name: "Institute Name", pending_fees: "Pending Fees", current_date: "Current Date" };

/* ══════════════════════════════════════════════════════════════════════════════
   Scheduled Messages Tab (main export)
   ══════════════════════════════════════════════════════════════════════════════ */
const ScheduledMessagesTab = ({ showToast }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [viewingMessage, setViewingMessage] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const limit = 10;

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (statusFilter) params.status = statusFilter;
      const { data } = await apiClient.get("/whatsapp/scheduled", { params });
      setMessages(data.messages || []);
      setTotal(data.total || 0);
    } catch { showToast("Failed to load scheduled messages", "error"); }
    setLoading(false);
  }, [page, statusFilter, showToast]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/whatsapp/scheduled/${deleteTarget._id}`);
      showToast("Scheduled message cancelled");
      setDeleteTarget(null);
      fetchMessages();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to cancel", "error");
    }
    setDeleting(false);
  };

  const handleEdit = (msg) => {
    setEditingMessage(msg);
    setShowForm(true);
  };

  const handleFormClose = (refresh) => {
    setShowForm(false);
    setEditingMessage(null);
    if (refresh) fetchMessages();
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="wa-scheduled">
      {/* Header */}
      <div className="wa-scheduled__header">
        <div className="wa-scheduled__header-text">
          <h3 className="wa-scheduled__title"><CalendarClock size={20} /> Scheduled Messages</h3>
          <p className="wa-scheduled__subtitle">
            Schedule WhatsApp messages for events, holidays, batch starts, and more
          </p>
        </div>
        <AppButton onClick={() => { setEditingMessage(null); setShowForm(true); }}>
          <Plus size={14} /> Schedule Message
        </AppButton>
      </div>

      {/* Filters */}
      <div className="wa-scheduled__filters">
        <Form.Select
          size="sm"
          className="wa-scheduled__filter-select"
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All Statuses</option>
          <option value="scheduled">Scheduled</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="cancelled">Cancelled</option>
        </Form.Select>
        <span className="wa-scheduled__count">{total} message{total !== 1 ? "s" : ""}</span>
      </div>

      {/* List */}
      {loading ? <Loader /> : messages.length === 0 ? (
        <EmptyState
          icon={<CalendarClock size={40} />}
          title="No Scheduled Messages"
          description="Schedule your first WhatsApp message for events, holidays, or batch reminders."
        />
      ) : (
        <div className="wa-scheduled__list">
          {messages.map(msg => (
            <ScheduledMessageCard
              key={msg._id}
              message={msg}
              onView={() => setViewingMessage(msg)}
              onEdit={() => handleEdit(msg)}
              onDelete={() => setDeleteTarget(msg)}
            />
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="wa-scheduled__pagination">
              <AppButton
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
              >Previous</AppButton>
              <span className="wa-scheduled__page-info">Page {page} of {totalPages}</span>
              <AppButton
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >Next</AppButton>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showForm && (
        <ScheduleMessageForm
          editingMessage={editingMessage}
          onClose={handleFormClose}
          showToast={showToast}
        />
      )}

      {/* View Detail Modal */}
      {viewingMessage && (
        <ScheduledMessageDetail
          message={viewingMessage}
          onClose={() => setViewingMessage(null)}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => { if (!deleting) setDeleteTarget(null); }}
        onConfirm={handleDelete}
        title="Cancel Scheduled Message"
        variant="warning"
        confirmText="Cancel Message"
        cancelText="Keep"
        loading={deleting}
        message={
          <div>
            <p>Are you sure you want to cancel this scheduled message?</p>
            {deleteTarget && (
              <div className="wa-scheduled__delete-info">
                <strong>{deleteTarget.title}</strong>
                <span>Scheduled for {moment(deleteTarget.scheduledAt).format("MMM DD, YYYY hh:mm A")}</span>
              </div>
            )}
          </div>
        }
      />
    </div>
  );
};

/* ── Scheduled Message Card ────────────────────────────────────────────────── */
const ScheduledMessageCard = ({ message, onView, onEdit, onDelete }) => {
  const statusInfo = STATUS_MAP[message.status] || STATUS_MAP.scheduled;
  const audienceInfo = AUDIENCE_TYPES.find(a => a.value === message.audienceType);
  const AudienceIcon = audienceInfo?.icon || Users;
  const isPast = new Date(message.scheduledAt) < new Date();
  const canEdit = message.status === "scheduled";
  const canDelete = !["sent", "processing"].includes(message.status);

  return (
    <div className={`wa-sched-card ${message.status === 'cancelled' ? 'wa-sched-card--dimmed' : ''}`}>
      <div className="wa-sched-card__top">
        <div className="wa-sched-card__title-row">
          <h4 className="wa-sched-card__title">{message.title}</h4>
          <span className={`wa-sched-card__status wa-sched-card__status--${message.status}`}>
            {statusInfo.label}
          </span>
        </div>
        {message.description && (
          <p className="wa-sched-card__desc">{message.description}</p>
        )}
      </div>

      <div className="wa-sched-card__meta">
        <div className="wa-sched-card__meta-item">
          <Clock size={13} />
          <span>{moment(message.scheduledAt).format("MMM DD, YYYY · hh:mm A")}</span>
          {message.status === 'scheduled' && (
            <span className="wa-sched-card__relative-time">
              ({isPast ? "overdue" : moment(message.scheduledAt).fromNow()})
            </span>
          )}
        </div>
        <div className="wa-sched-card__meta-item">
          <AudienceIcon size={13} />
          <span>{audienceInfo?.label || message.audienceType}</span>
          {message.courseId?.name && <span className="wa-sched-card__meta-dim">— {message.courseId.name}</span>}
          {message.batchId?.name && <span className="wa-sched-card__meta-dim">— {message.batchId.name}</span>}
        </div>
        <div className="wa-sched-card__meta-item">
          <FileText size={13} />
          <span>{message.templateName}</span>
        </div>
        <div className="wa-sched-card__meta-item">
          <Users size={13} />
          <span>{message.recipientCount} recipient{message.recipientCount !== 1 ? "s" : ""}</span>
          {message.sentCount > 0 && (
            <span className="wa-sched-card__meta-dim">· {message.sentCount} sent</span>
          )}
        </div>
      </div>

      <div className="wa-sched-card__actions">
        <button className="wa-sched-card__action-btn" onClick={onView} title="View details">
          <Eye size={14} /> View
        </button>
        {canEdit && (
          <button className="wa-sched-card__action-btn wa-sched-card__action-btn--edit" onClick={onEdit} title="Edit">
            <Edit3 size={14} /> Edit
          </button>
        )}
        {canDelete && (
          <button className="wa-sched-card__action-btn wa-sched-card__action-btn--delete" onClick={onDelete} title="Cancel">
            <Trash2 size={14} /> Cancel
          </button>
        )}
      </div>
    </div>
  );
};

/* ── Scheduled Message Detail Modal ────────────────────────────────────────── */
const ScheduledMessageDetail = ({ message, onClose }) => {
  const statusInfo = STATUS_MAP[message.status] || STATUS_MAP.scheduled;
  const audienceInfo = AUDIENCE_TYPES.find(a => a.value === message.audienceType);

  return (
    <Modal show centered size="lg" onHide={onClose} className="wa-scheduled-modal">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1rem", fontWeight: 600 }}>
          <CalendarClock size={18} /> Scheduled Message Details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="wa-sched-detail">
          <div className="wa-sched-detail__row">
            <span className="wa-sched-detail__label">Title</span>
            <span>{message.title}</span>
          </div>
          {message.description && (
            <div className="wa-sched-detail__row">
              <span className="wa-sched-detail__label">Description</span>
              <span>{message.description}</span>
            </div>
          )}
          <div className="wa-sched-detail__row">
            <span className="wa-sched-detail__label">Status</span>
            <span className={`wa-sched-card__status wa-sched-card__status--${message.status}`}>{statusInfo.label}</span>
          </div>
          <div className="wa-sched-detail__row">
            <span className="wa-sched-detail__label">Scheduled At</span>
            <span>{moment(message.scheduledAt).format("MMM DD, YYYY · hh:mm A")}</span>
          </div>
          <div className="wa-sched-detail__row">
            <span className="wa-sched-detail__label">Template</span>
            <span>{message.templateName} ({message.languageCode})</span>
          </div>
          <div className="wa-sched-detail__row">
            <span className="wa-sched-detail__label">Audience</span>
            <span>{audienceInfo?.label || message.audienceType}</span>
          </div>
          <div className="wa-sched-detail__row">
            <span className="wa-sched-detail__label">Recipient Filter</span>
            <span style={{ textTransform: "capitalize" }}>{message.recipientStatus || "active"}</span>
          </div>
          {message.courseId?.name && (
            <div className="wa-sched-detail__row">
              <span className="wa-sched-detail__label">Course</span>
              <span>{message.courseId.name}{message.courseId.title ? ` (${message.courseId.title})` : ""}</span>
            </div>
          )}
          {message.batchId?.name && (
            <div className="wa-sched-detail__row">
              <span className="wa-sched-detail__label">Batch</span>
              <span>{message.batchId.name}</span>
            </div>
          )}
          <div className="wa-sched-detail__row">
            <span className="wa-sched-detail__label">Recipients</span>
            <span>{message.recipientCount}</span>
          </div>
          {message.sentCount > 0 && (
            <div className="wa-sched-detail__row">
              <span className="wa-sched-detail__label">Sent</span>
              <span>{message.sentCount}</span>
            </div>
          )}
          {message.executedAt && (
            <div className="wa-sched-detail__row">
              <span className="wa-sched-detail__label">Executed At</span>
              <span>{moment(message.executedAt).format("MMM DD, YYYY · hh:mm A")}</span>
            </div>
          )}
          {message.executionError && (
            <div className="wa-alert wa-alert--warning" style={{ marginTop: 8 }}>
              <AlertTriangle size={14} /> {message.executionError}
            </div>
          )}
          {message.createdBy?.name && (
            <div className="wa-sched-detail__row">
              <span className="wa-sched-detail__label">Created By</span>
              <span>{message.createdBy.name}</span>
            </div>
          )}
          <div className="wa-sched-detail__row">
            <span className="wa-sched-detail__label">Created</span>
            <span>{moment(message.createdAt).format("MMM DD, YYYY · hh:mm A")}</span>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <AppButton variant="outline" onClick={onClose}>Close</AppButton>
      </Modal.Footer>
    </Modal>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
   Schedule Message Form (Create / Edit) — Full-page Modal
   ══════════════════════════════════════════════════════════════════════════════ */
const ScheduleMessageForm = ({ editingMessage, onClose, showToast }) => {
  // ── Data loading ──
  const [templates, setTemplates] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);

  // ── Form fields ──
  const [title, setTitle] = useState(editingMessage?.title || "");
  const [description, setDescription] = useState(editingMessage?.description || "");
  const [scheduledDate, setScheduledDate] = useState(
    editingMessage?.scheduledAt ? moment(editingMessage.scheduledAt).format("YYYY-MM-DD") : ""
  );
  const [scheduledTime, setScheduledTime] = useState(
    editingMessage?.scheduledAt ? moment(editingMessage.scheduledAt).format("HH:mm") : ""
  );

  // ── Audience ──
  const [audienceType, setAudienceType] = useState(editingMessage?.audienceType || "all_students");
  const [recipientStatus, setRecipientStatus] = useState(editingMessage?.recipientStatus || "active");
  const [courseId, setCourseId] = useState(editingMessage?.courseId?._id || editingMessage?.courseId || "");
  const [batchId, setBatchId] = useState(editingMessage?.batchId?._id || editingMessage?.batchId || "");
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentQuery, setStudentQuery] = useState("");
  const [studentResults, setStudentResults] = useState([]);
  const [studentSearching, setStudentSearching] = useState(false);
  const searchTimer = useRef(null);

  // ── External numbers ──
  const [externalNumbersText, setExternalNumbersText] = useState(
    editingMessage?.externalNumbers?.join("\n") || ""
  );
  const parsedExternalNumbers = useMemo(() => {
    if (!externalNumbersText.trim()) return [];
    return externalNumbersText
      .split(/[\n,;]+/)
      .map(n => n.trim().replace(/[\s()+\u002D]/g, ''))
      .filter(n => n.length >= 10);
  }, [externalNumbersText]);

  // ── Template ──
  const [templateName, setTemplateName] = useState(editingMessage?.templateName || "");
  const [paramValues, setParamValues] = useState({});
  const [paramModes, setParamModes] = useState({});

  // ── Recipient count ──
  const [recipientCount, setRecipientCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const countTimer = useRef(null);

  // ── Submission ──
  const [saving, setSaving] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // ── Derived ──
  const selectedTemplate = useMemo(() => templates.find(t => t.name === templateName) || null, [templates, templateName]);
  const templateVars = useMemo(() => selectedTemplate ? _extractTemplateVars(selectedTemplate) : { header: [], body: [], button: [] }, [selectedTemplate]);
  const totalParams = templateVars.header.length + templateVars.body.length + templateVars.button.length;

  const filteredBatches = useMemo(() => {
    if (audienceType === "batch" && courseId) {
      return batches.filter(b => (b.course?._id || b.course) === courseId);
    }
    return batches;
  }, [batches, audienceType, courseId]);

  const allParamsFilled = totalParams === 0 || [
    ...templateVars.header.map(p => `header_${p.key}`),
    ...templateVars.body.map(p => `body_${p.key}`),
    ...templateVars.button.map(p => `button_${p.key}`),
  ].every(k => {
    if (paramModes[k] === 'dynamic' && paramValues[k]) return true;
    return paramModes[k] !== 'dynamic' && paramValues[k]?.trim();
  });

  const audienceReady = (
    (audienceType === "all_students") ||
    (audienceType === "course" && courseId) ||
    (audienceType === "batch" && batchId) ||
    (audienceType === "specific_students" && selectedStudents.length > 0) ||
    (audienceType === "external_numbers" && parsedExternalNumbers.length > 0) ||
    (audienceType === "trainers")
  );

  const scheduleReady = title.trim() && scheduledDate && scheduledTime && new Date(`${scheduledDate}T${scheduledTime}`) > new Date();
  const canSubmit = scheduleReady && templateName && allParamsFilled && audienceReady;

  // ── Load initial data ──
  useEffect(() => {
    (async () => {
      try {
        const [tplRes, optRes] = await Promise.all([
          apiClient.get("/whatsapp/templates"),
          apiClient.get("/whatsapp/broadcast/options"),
        ]);
        setTemplates((tplRes.data.templates || []).filter(t => t.status === "APPROVED"));
        setCourses(optRes.data.courses || []);
        setBatches(optRes.data.batches || []);
      } catch { /* ignore */ }
      setInitialLoading(false);
    })();
  }, []);

  // ── Restore paramConfig when editing ──
  useEffect(() => {
    if (editingMessage?.paramConfig && selectedTemplate) {
      const pc = editingMessage.paramConfig;
      const modes = {};
      const vals = {};
      const restore = (entries, prefix) => {
        (entries || []).forEach(e => {
          const k = `${prefix}_${e.key}`;
          modes[k] = e.type || "static";
          vals[k] = e.type === "dynamic" ? (e.variable || "") : (e.value || "");
        });
      };
      restore(pc.header, "header");
      restore(pc.body, "body");
      restore(pc.button, "button");
      setParamModes(modes);
      setParamValues(vals);
    }
  }, [editingMessage, selectedTemplate]);

  // ── Reset params when template changes (only for new messages) ──
  useEffect(() => {
    if (!editingMessage) { setParamValues({}); setParamModes({}); }
  }, [templateName, editingMessage]);

  // ── Fetch recipient count ──
  useEffect(() => {
    if (countTimer.current) clearTimeout(countTimer.current);
    setRecipientCount(null);
    if (!audienceReady) return;

    if (audienceType === "external_numbers") {
      setRecipientCount(parsedExternalNumbers.length);
      return;
    }

    countTimer.current = setTimeout(async () => {
      setCountLoading(true);
      try {
        const body = { audienceType, recipientStatus };
        if (audienceType === "course") body.courseId = courseId;
        if (audienceType === "batch") body.batchId = batchId;
        if (audienceType === "specific_students") body.studentIds = selectedStudents.map(s => s._id);
        const { data } = await apiClient.post("/whatsapp/scheduled/estimate-recipients", body);
        setRecipientCount(data.count);
      } catch { setRecipientCount(null); }
      setCountLoading(false);
    }, 400);

    return () => { if (countTimer.current) clearTimeout(countTimer.current); };
  }, [audienceType, courseId, batchId, selectedStudents, audienceReady, recipientStatus, parsedExternalNumbers]);

  // ── Student search ──
  const handleStudentSearch = useCallback((value) => {
    setStudentQuery(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (value.trim().length < 2) { setStudentResults([]); setStudentSearching(false); return; }
    setStudentSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const { data } = await apiClient.get(`/whatsapp/students/search?q=${encodeURIComponent(value.trim())}`);
        setStudentResults(data.students || []);
      } catch { setStudentResults([]); }
      setStudentSearching(false);
    }, 350);
  }, []);

  const addStudent = (s) => {
    if (!selectedStudents.find(x => x._id === s._id)) {
      setSelectedStudents(prev => [...prev, s]);
    }
    setStudentQuery("");
    setStudentResults([]);
  };

  const removeStudent = (id) => {
    setSelectedStudents(prev => prev.filter(s => s._id !== id));
  };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
      const payload = {
        title: title.trim(),
        description: description.trim(),
        templateName,
        languageCode: selectedTemplate?.language || "en",
        scheduledAt,
        audienceType,
        recipientStatus,
      };

      // Build paramConfig
      if (totalParams > 0) {
        const buildEntries = (params, prefix) => params.map(p => {
          const k = `${prefix}_${p.key}`;
          const mode = paramModes[k] || "static";
          return {
            key: p.key,
            type: mode,
            variable: mode === "dynamic" ? (paramValues[k] || "") : "",
            value: mode === "static" ? (paramValues[k] || "") : "",
            ...(p.buttonIndex !== undefined ? { buttonIndex: p.buttonIndex } : {}),
          };
        });
        payload.paramConfig = {
          header: buildEntries(templateVars.header, "header"),
          body: buildEntries(templateVars.body, "body"),
          button: buildEntries(templateVars.button, "button"),
        };
        payload.components = _buildComponents(templateVars, paramValues);
      }

      if (audienceType === "course") payload.courseId = courseId;
      if (audienceType === "batch") payload.batchId = batchId;
      if (audienceType === "specific_students") payload.studentIds = selectedStudents.map(s => s._id);
      if (audienceType === "external_numbers") payload.externalNumbers = parsedExternalNumbers;

      if (editingMessage) {
        await apiClient.put(`/whatsapp/scheduled/${editingMessage._id}`, payload);
        showToast("Scheduled message updated");
      } else {
        await apiClient.post("/whatsapp/scheduled", payload);
        showToast("Message scheduled successfully");
      }
      setShowConfirmModal(false);
      onClose(true);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to save", "error");
    }
    setSaving(false);
  };

  if (initialLoading) return (
    <Modal show centered size="xl" onHide={() => onClose(false)} className="wa-scheduled-modal">
      <Modal.Body><Loader /></Modal.Body>
    </Modal>
  );

  return (
    <>
      <Modal show centered size="xl" onHide={() => { if (!saving) onClose(false); }} className="wa-scheduled-modal">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 600 }}>
            <CalendarClock size={18} /> {editingMessage ? "Edit Scheduled Message" : "Schedule New Message"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="wa-sched-form__body">
          <div className="wa-sched-form">
            {/* ── Step 1: Message Info & Schedule ── */}
            <AppCard>
              <AppCardHeader>
                <AppCardTitle><CalendarClock size={18} /> Step 1 — Message Info &amp; Schedule</AppCardTitle>
                <p className="settings-card-desc">Give your message a name and set when it should be sent</p>
              </AppCardHeader>
              <AppCardContent>
                <div className="wa-sched-form__grid">
                  <Form.Group>
                    <Form.Label>Title <span className="wa-required">*</span></Form.Label>
                    <Form.Control
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                      placeholder="e.g., Diwali Greeting, New Batch Announcement..."
                      maxLength={200}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Description <span className="wa-optional">(optional)</span></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      placeholder="Internal notes about this message..."
                      maxLength={500}
                    />
                  </Form.Group>
                </div>
                <div className="wa-sched-form__datetime">
                  <Form.Group className="wa-sched-form__date-group">
                    <Form.Label>Date <span className="wa-required">*</span></Form.Label>
                    <Form.Control
                      type="date"
                      value={scheduledDate}
                      onChange={e => setScheduledDate(e.target.value)}
                      min={moment().format("YYYY-MM-DD")}
                    />
                  </Form.Group>
                  <Form.Group className="wa-sched-form__time-group">
                    <Form.Label>Time <span className="wa-required">*</span></Form.Label>
                    <Form.Control
                      type="time"
                      value={scheduledTime}
                      onChange={e => setScheduledTime(e.target.value)}
                    />
                  </Form.Group>
                </div>
                {scheduledDate && scheduledTime && (
                  <div className="wa-sched-form__schedule-preview">
                    <Clock size={14} />
                    <span>
                      Will be sent on <strong>{moment(`${scheduledDate}T${scheduledTime}`).format("dddd, MMMM DD, YYYY")} at {moment(`${scheduledDate}T${scheduledTime}`).format("hh:mm A")}</strong>
                      {' '}({moment(`${scheduledDate}T${scheduledTime}`).fromNow()})
                    </span>
                  </div>
                )}
              </AppCardContent>
            </AppCard>

            {/* ── Step 2: Select Audience ── */}
            <AppCard>
              <AppCardHeader>
                <AppCardTitle><Users size={18} /> Step 2 — Select Audience</AppCardTitle>
                <p className="settings-card-desc">Choose who will receive this message</p>
              </AppCardHeader>
              <AppCardContent>
                <div className="wa-bc-audience-grid">
                  {AUDIENCE_TYPES.map(a => {
                    const Icon = a.icon;
                    return (
                      <div
                        key={a.value}
                        className={`wa-bc-audience-card ${audienceType === a.value ? "wa-bc-audience-card--active" : ""}`}
                        onClick={() => {
                          setAudienceType(a.value);
                          setCourseId(""); setBatchId(""); setSelectedStudents([]); setExternalNumbersText("");
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => e.key === "Enter" && setAudienceType(a.value)}
                      >
                        <div className="wa-bc-audience-card__icon"><Icon size={20} /></div>
                        <div className="wa-bc-audience-card__label">{a.label}</div>
                        <div className="wa-bc-audience-card__desc">{a.desc}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Recipient Status Filter */}
                {audienceType !== "external_numbers" && (
                  <div className="wa-sched-form__status-filter">
                    <Form.Label className="wa-sched-form__filter-label">Send to:</Form.Label>
                    <div className="wa-sched-form__status-btns">
                      {RECIPIENT_STATUS_OPTIONS.map(opt => {
                        const Icon = opt.icon;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            className={`wa-mode-btn ${recipientStatus === opt.value ? "wa-mode-btn--active" : ""}`}
                            onClick={() => setRecipientStatus(opt.value)}
                          >
                            <Icon size={13} /> {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Course Selector */}
                {audienceType === "course" && (
                  <div className="wa-bc-filter-section">
                    <Form.Group>
                      <Form.Label>Select Course</Form.Label>
                      <Form.Select value={courseId} onChange={e => setCourseId(e.target.value)}>
                        <option value="">Choose a course...</option>
                        {courses.map(c => <option key={c._id} value={c._id}>{c.name}{c.title ? ` (${c.title})` : ""}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </div>
                )}

                {/* Batch Selector */}
                {audienceType === "batch" && (
                  <div className="wa-bc-filter-section">
                    <Form.Group className="mb-2">
                      <Form.Label>Filter by Course (optional)</Form.Label>
                      <Form.Select value={courseId} onChange={e => { setCourseId(e.target.value); setBatchId(""); }}>
                        <option value="">All Courses</option>
                        {courses.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Select Batch</Form.Label>
                      <Form.Select value={batchId} onChange={e => setBatchId(e.target.value)}>
                        <option value="">Choose a batch...</option>
                        {filteredBatches.map(b => (
                          <option key={b._id} value={b._id}>
                            {b.name}{b.course?.name ? ` — ${b.course.name}` : ""}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                )}

                {/* Student Picker */}
                {audienceType === "specific_students" && (
                  <div className="wa-bc-filter-section">
                    <Form.Label>Search &amp; Select Students</Form.Label>
                    <div className="wa-bc-student-search-box">
                      <Search size={15} className="wa-bc-student-search-icon" />
                      <input
                        type="text"
                        className="wa-bc-student-search-input"
                        placeholder="Search by name or mobile number..."
                        value={studentQuery}
                        onChange={e => handleStudentSearch(e.target.value)}
                      />
                      {studentSearching && <Spinner animation="border" size="sm" />}
                    </div>

                    {studentResults.length > 0 && (
                      <div className="wa-bc-student-results">
                        {studentResults.filter(s => !selectedStudents.find(x => x._id === s._id)).map(s => (
                          <div key={s._id} className="wa-bc-student-result" onClick={() => addStudent(s)} role="button" tabIndex={0} onKeyDown={e => e.key === "Enter" && addStudent(s)}>
                            <User size={14} />
                            <span className="wa-bc-student-result__name">{s.name}</span>
                            <span className="wa-bc-student-result__meta">{s.mobile}</span>
                            {s.course?.name && <span className="wa-bc-student-result__meta">{s.course.name}</span>}
                          </div>
                        ))}
                      </div>
                    )}

                    {selectedStudents.length > 0 && (
                      <div className="wa-bc-selected-students">
                        <div className="wa-bc-selected-students__label">
                          Selected ({selectedStudents.length})
                          <button className="wa-bc-clear-all" onClick={() => setSelectedStudents([])}>Clear all</button>
                        </div>
                        <div className="wa-bc-chips">
                          {selectedStudents.map(s => (
                            <div key={s._id} className="wa-bc-chip">
                              <User size={12} />
                              <span>{s.name}</span>
                              <button className="wa-bc-chip__remove" onClick={() => removeStudent(s._id)} title="Remove"><X size={12} /></button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* External Numbers */}
                {audienceType === "external_numbers" && (
                  <div className="wa-bc-filter-section">
                    <label className="wa-form-label" style={{ marginBottom: 6 }}>Phone Numbers</label>
                    <textarea
                      className="wa-form-input wa-form-textarea"
                      rows={4}
                      value={externalNumbersText}
                      onChange={e => setExternalNumbersText(e.target.value)}
                      placeholder={"Enter phone numbers (one per line)\n919876543210\n918765432109"}
                    />
                    <div className="wa-form-hint" style={{ marginTop: 6 }}>
                      Include country code (e.g. 91 for India).
                    </div>
                    {parsedExternalNumbers.length > 0 && (
                      <div className="wa-form-hint" style={{ marginTop: 4 }}>
                        <strong>{parsedExternalNumbers.length}</strong> valid number{parsedExternalNumbers.length !== 1 ? "s" : ""} detected
                      </div>
                    )}
                  </div>
                )}

                {/* Recipient Count */}
                {audienceReady && (
                  <div className="wa-bc-recipient-count">
                    <Users size={14} />
                    {countLoading ? (
                      <span>Counting recipients…</span>
                    ) : recipientCount !== null ? (
                      <span><strong>{recipientCount}</strong> recipient{recipientCount !== 1 ? "s" : ""} will receive this message</span>
                    ) : (
                      <span>Unable to estimate recipients</span>
                    )}
                  </div>
                )}
              </AppCardContent>
            </AppCard>

            {/* ── Step 3: Template Selection & Variables ── */}
            <AppCard>
              <AppCardHeader>
                <AppCardTitle><FileText size={18} /> Step 3 — Select Template &amp; Fill Variables</AppCardTitle>
                <p className="settings-card-desc">Choose an approved WhatsApp template</p>
              </AppCardHeader>
              <AppCardContent>
                {templates.length === 0 ? (
                  <EmptyState
                    icon={<FileText size={32} />}
                    title="No Approved Templates"
                    description='Sync templates from Meta in the Templates tab first.'
                  />
                ) : (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Template <span className="wa-required">*</span></Form.Label>
                      <Form.Select value={templateName} onChange={e => setTemplateName(e.target.value)}>
                        <option value="">Select a template...</option>
                        {templates.map(t => (
                          <option key={t._id} value={t.name}>
                            {t.name} ({t.language}) — {t.category || "MARKETING"}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    {/* Variable inputs */}
                    {selectedTemplate && totalParams > 0 && (
                      <div className="wa-bc-vars">
                        <div className="wa-bc-vars__title">Template Variables ({totalParams})</div>
                        <div className="wa-sched-form__var-hint">
                          For each variable, choose <strong>Dynamic</strong> (per student) or <strong>Static</strong> (same for everyone).
                        </div>
                        {templateVars.header.length > 0 && (
                          <VarGroup label="Header" params={templateVars.header} prefix="header" paramModes={paramModes} paramValues={paramValues} setParamModes={setParamModes} setParamValues={setParamValues} />
                        )}
                        {templateVars.body.length > 0 && (
                          <VarGroup label="Body" params={templateVars.body} prefix="body" paramModes={paramModes} paramValues={paramValues} setParamModes={setParamModes} setParamValues={setParamValues} />
                        )}
                        {templateVars.button.length > 0 && (
                          <div className="wa-bc-vars__group">
                            <div className="wa-bc-vars__group-label">Buttons</div>
                            {templateVars.button.map(p => {
                              const k = `button_${p.key}`;
                              return (
                                <Form.Group key={k} className="mb-2">
                                  <Form.Label className="wa-bc-var-label">{p.label}</Form.Label>
                                  {p.baseUrl && (
                                    <div className="wa-sched-form__base-url">
                                      Base URL: <strong>{p.baseUrl}</strong>/<span className="wa-sched-form__suffix">{"{{suffix}}"}</span>
                                    </div>
                                  )}
                                  <Form.Control
                                    size="sm"
                                    value={paramValues[k] || ""}
                                    onChange={e => setParamValues(pv => ({ ...pv, [k]: e.target.value }))}
                                    placeholder={p.example || "Enter suffix..."}
                                  />
                                </Form.Group>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Live Preview */}
                    {selectedTemplate && (
                      <TemplatePreview template={selectedTemplate} vars={templateVars} values={paramValues} modes={paramModes} />
                    )}
                  </>
                )}
              </AppCardContent>
            </AppCard>
          </div>
        </Modal.Body>
        <Modal.Footer className="wa-sched-form__footer">
          <AppButton variant="outline" onClick={() => onClose(false)} disabled={saving}>Cancel</AppButton>
          <AppButton onClick={() => setShowConfirmModal(true)} disabled={!canSubmit || saving}>
            <CalendarClock size={14} /> {editingMessage ? "Update Schedule" : "Schedule Message"}
          </AppButton>
        </Modal.Footer>
      </Modal>

      {/* Confirm */}
      <ConfirmModal
        show={showConfirmModal}
        onHide={() => { if (!saving) setShowConfirmModal(false); }}
        onConfirm={handleSubmit}
        title={editingMessage ? "Confirm Update" : "Confirm Schedule"}
        confirmText={editingMessage ? "Update" : "Schedule"}
        cancelText="Cancel"
        loading={saving}
        message={
          <div className="wa-sched-confirm">
            <div className="wa-bc-summary">
              <div className="wa-bc-summary__row">
                <span className="wa-bc-summary__label">Title</span>
                <span>{title}</span>
              </div>
              <div className="wa-bc-summary__row">
                <span className="wa-bc-summary__label">When</span>
                <span>{scheduledDate && scheduledTime ? moment(`${scheduledDate}T${scheduledTime}`).format("MMM DD, YYYY · hh:mm A") : "—"}</span>
              </div>
              <div className="wa-bc-summary__row">
                <span className="wa-bc-summary__label">Audience</span>
                <span>{AUDIENCE_TYPES.find(a => a.value === audienceType)?.label || audienceType}</span>
              </div>
              <div className="wa-bc-summary__row">
                <span className="wa-bc-summary__label">Recipients</span>
                <span>{recipientCount ?? "?"}</span>
              </div>
              <div className="wa-bc-summary__row">
                <span className="wa-bc-summary__label">Template</span>
                <span>{templateName}</span>
              </div>
            </div>
            {!editingMessage && (
              <div className="wa-alert wa-alert--info" style={{ marginTop: 10 }}>
                <CalendarClock size={14} />
                This message will be automatically sent at the scheduled time.
              </div>
            )}
          </div>
        }
      />
    </>
  );
};

/* ── Variable Group ────────────────────────────────────────────────────────── */
const VarGroup = ({ label, params, prefix, paramModes, paramValues, setParamModes, setParamValues }) => (
  <div className="wa-bc-vars__group">
    <div className="wa-bc-vars__group-label">{label}</div>
    {params.map(p => {
      const k = `${prefix}_${p.key}`;
      const mode = paramModes[k] || "static";
      return (
        <div key={k} className="mb-2">
          <Form.Label className="wa-bc-var-label">{p.label}</Form.Label>
          <div className="wa-sched-form__var-row">
            <div className="wa-sched-form__var-btns">
              <button type="button" className={`wa-mode-btn ${mode === "dynamic" ? "wa-mode-btn--active" : ""}`} onClick={() => { setParamModes(m => ({ ...m, [k]: "dynamic" })); setParamValues(pv => ({ ...pv, [k]: "" })); }}>Dynamic</button>
              <button type="button" className={`wa-mode-btn ${mode === "static" ? "wa-mode-btn--active" : ""}`} onClick={() => { setParamModes(m => ({ ...m, [k]: "static" })); setParamValues(pv => ({ ...pv, [k]: "" })); }}>Static</button>
            </div>
            {mode === "dynamic" ? (
              <Form.Select size="sm" className="wa-sched-form__var-select" value={paramValues[k] || ""} onChange={e => setParamValues(pv => ({ ...pv, [k]: e.target.value }))}>
                <option value="">-- Select variable --</option>
                {DYNAMIC_VARIABLES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
              </Form.Select>
            ) : (
              <Form.Control size="sm" value={paramValues[k] || ""} onChange={e => setParamValues(pv => ({ ...pv, [k]: e.target.value }))} placeholder={p.example || `Enter ${p.label}...`} />
            )}
          </div>
        </div>
      );
    })}
  </div>
);

/* ── Template Preview ──────────────────────────────────────────────────────── */
const TemplatePreview = ({ template, vars, values, modes = {} }) => {
  const hc = template.components?.find(c => c.type === "HEADER");
  const bc = template.components?.find(c => c.type === "BODY");
  const fc = template.components?.find(c => c.type === "FOOTER");
  const btc = template.components?.find(c => c.type === "BUTTONS");

  const previewValues = { ...values };
  Object.entries(modes).forEach(([k, mode]) => {
    if (mode === "dynamic" && values[k]) {
      previewValues[k] = `[${DYNAMIC_VAR_LABELS[values[k]] || values[k]}]`;
    }
  });

  const headerText = hc?.format === "TEXT" ? _fillPreview(hc.text, vars.header, previewValues, "header") : null;
  const bodyText = bc?.text ? _fillPreview(bc.text, vars.body, previewValues, "body") : null;
  const footerText = fc?.text || null;

  return (
    <div className="wa-bc-preview">
      <div className="wa-bc-preview__label">Preview</div>
      <div className="wa-bc-preview__bubble">
        {headerText && <div className="wa-bc-preview__header">{headerText}</div>}
        {bodyText && <div className="wa-bc-preview__body">{bodyText}</div>}
        {footerText && <div className="wa-bc-preview__footer">{footerText}</div>}
        {btc?.buttons?.length > 0 && (
          <div className="wa-bc-preview__buttons">
            {btc.buttons.map((btn, i) => <div key={btn.text || i} className="wa-bc-preview__btn">{btn.text}</div>)}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduledMessagesTab;
